import type { History } from "@sinch/components/application/RatelistBuilder/types";

/**
 * Moves backward in history by one step.
 *
 */
export const back = <T = any,>(historyStore: History<T>) => {
  if (historyStore.past.length === 0) {
    return null;
  }

  const previous = historyStore.past.at(-1);
  const newPast = historyStore.past.slice(0, -1);

  if (!previous) {
    return null;
  }

  return {
    past: newPast,
    current: structuredClone(previous),
    future: [historyStore.current, ...historyStore.future],
  };
};

/**
 * Moves forward in history by one step.
 */
export const forward = <T = any,>(historyStore: History<T>) => {
  if (historyStore.future.length === 0) {
    return null;
  }

  const [next, ...newFuture] = historyStore.future;

  return {
    past: [...historyStore.past, historyStore.current],
    current: structuredClone(next),
    future: newFuture,
  };
};

/**
 * Clears history and sets a new current state.
 */
export const clear = <T = any,>(current: T) => ({
  past: [],
  current: structuredClone(current),
  future: [],
});

/**
 * Creates a snapshot function that can be used to record state changes.
 */
export const snapshot = <T = any,>(historyStore: History<T>) => {
  const frozenCurrent = structuredClone(historyStore.current);

  return (value: T): History<T> => {
    if (value === historyStore.current) {
      return historyStore;
    }

    return {
      past: [...historyStore.past, frozenCurrent],
      current: { ...value },
      future: [],
    };
  };
};
